/* eslint-disable indent */
import React, { useEffect, useState } from 'react';
import { DateTime } from 'luxon';
import { IPayStub } from 'src/interfaces';

import { useTypedSelector } from 'src/hooks/useTypedSelector';
import paymentsService from 'src/services/payments.service';
import { AmountInput, Input, Wrapper } from './styles';
import { DefaultTable } from '../default-table';

const columns = [
  {
    selector: (row: IPayStub) => row.date,
  },
  {
    selector: (row: IPayStub) => row.hours,
  },
  {
    selector: (row: IPayStub) => row.amount,
  },
  {
    selector: (row: IPayStub) => row.taxes,
  },
  {
    selector: (row: IPayStub) => row.deductions,
  },
  {
    selector: (row: IPayStub) => row.benefits,
  },
  {
    selector: (row: IPayStub) => row.netPay,
  },
  {
    name: 'Link',
    button: true,
    cell: (row: IPayStub) => (
      <a href={paymentsService.getPayStubPdf(row.payStatementNumber, true)} target="_blank" rel="noopener noreferrer">
        Download
      </a>
    ),
  },
];

// eslint-disable-next-line no-unused-vars
const PayHistoryTable: React.FC<{ onFilter: (pst: IPayStub[]) => void }> = ({ onFilter }) => {
  const [items, setData] = useState<IPayStub[]>([]);
  const [dataFiltered, setDataFiltered] = useState<IPayStub[]>([]);
  const [date, setDate] = useState<string>('');
  const [hours, setHours] = useState<string>('');
  const [amount, setAmount] = useState<string>('');
  const [taxes, setTaxes] = useState<string>('');
  const [deductions, setDeductions] = useState<string>('');
  const [benefits, setBenefits] = useState<string>('');
  const [netPay, setNetPay] = useState<string>('');

  const { data, loading } = useTypedSelector(state => state.payments);

  const formatRows = async (paystubItems: IPayStub[]) => {
    const newRows: IPayStub[] = [];
    paystubItems.forEach(item => {
      newRows.push({
        id: item.id,
        payStatementNumber: item.payStatementNumber,
        date: DateTime.fromISO(item.date).toFormat('MM/dd/yyyy').toString(),
        hours: item.hours ? item.hours.toString() : '',
        amount: item.amount ? ` ${item.amount}` : '$0.00',
        taxes: item.taxes ? ` ${item.taxes}` : '$0.00',
        deductions: item.deductions ? ` ${item.deductions}` : '$0.00',
        benefits: item.benefits ? ` ${item.benefits}` : '$0.00',
        netPay: item.netPay ? ` ${item.netPay}` : '$0.00',
      });
    });
    setData(newRows);
    setDataFiltered(newRows);
  };

  useEffect(() => {
    formatRows(data.payments);
  }, [data]);

  const filterData = async () => {
    const newFilteredData = items.length
      ? items.filter(
          item =>
            item.date.includes(date) &&
            item.hours.includes(hours) &&
            item.amount.includes(amount) &&
            item.taxes.includes(taxes) &&
            item.deductions.includes(deductions) &&
            item.benefits.includes(benefits) &&
            item.netPay.includes(netPay)
        )
      : [];
    setDataFiltered(newFilteredData);
  };

  useEffect(() => {
    if (items.length) {
      filterData();
    }
  }, [hours, date, amount, taxes, deductions, benefits, netPay]);

  const customStyles = {
    headRow: {
      style: {
        border: 'none',
      },
    },
    headCells: {
      style: {
        color: '#9399B2',
        font: 'normal normal bold 16px Biotif',
      },
    },
    rows: {
      style: {
        font: 'normal normal normal 14px Biotif',
        color: '#1B1D35',
        borderRadius: '5px',
        '&:not(:last-of-type)': {
          borderBottomWidth: '0px',
        },
      },
      stripedStyle: {
        color: '#1B1D35',
        backgroundColor: '#F3F4F8',
      },
      highlightOnHoverStyle: {
        backgroundColor: 'rgb(230, 244, 244)',
        borderBottomColor: '#FFFFFF',
        borderRadius: '25px',
        outline: '1px solid #FFFFFF',
      },
    },
    cells: {
      style: {
        justifyContent: 'left',
        '&:not(:nth-of-type(2))': {
          justifyContent: 'right',
        },
        '&:first-of-type': {
          justifyContent: 'left',
        },
        '&:last-of-type': {
          justifyContent: 'center',
        },
      },
    },
    pagination: {
      style: {
        border: 'none',
        svg: {
          fill: 'rgba(0,0,0,0.54)',
        },
      },
    },
  };

  useEffect(() => {
    onFilter(dataFiltered);
  }, [dataFiltered]);

  return (
    <>
      <Wrapper>
        <Input
          label="Date"
          placeholder="Filter..."
          onChange={(e: { target: { value: React.SetStateAction<string> } }) => setDate(e.target.value)}
        />
        <Input
          label="Hours"
          placeholder="Filter..."
          onChange={(e: { target: { value: React.SetStateAction<string> } }) => setHours(e.target.value)}
        />
        <AmountInput
          label="Amount"
          placeholder="$0"
          onChange={(e: { target: { value: React.SetStateAction<string> } }) => setAmount(e.target.value)}
        />
        <Input
          label="Taxes"
          placeholder="Filter..."
          onChange={(e: { target: { value: React.SetStateAction<string> } }) => setTaxes(e.target.value)}
        />
        <Input
          label="Deductions"
          placeholder="Filter..."
          onChange={(e: { target: { value: React.SetStateAction<string> } }) => setDeductions(e.target.value)}
        />
        <Input
          label="Benefits"
          placeholder="Filter..."
          onChange={(e: { target: { value: React.SetStateAction<string> } }) => setBenefits(e.target.value)}
        />
        <Input
          label="Net Pay"
          placeholder="Filter..."
          onChange={(e: { target: { value: React.SetStateAction<string> } }) => setNetPay(e.target.value)}
        />
      </Wrapper>

      <DefaultTable
        progressPending={loading}
        columns={columns}
        customStyles={customStyles}
        data={dataFiltered}
        noTableHead
      />
    </>
  );
};

export default PayHistoryTable;
